import { motion } from "framer-motion";
import { MONORAIL } from "../lib/monorail";
import { IMAGES } from "../lib/tokens";

/**
 * "The Rest of the World" — the founder's Design section, ported.
 *
 * Every sealed Place gets its exterior shot, cooled and dimmed so it reads as
 * lamplit-but-shut, its clock position, its tagline and its Practice. Copy comes
 * straight from `lib/monorail.js`, which holds his verbatim words, so the cards
 * and the tour can never drift apart.
 *
 * Founder's ruling: ALL sealed Places read "Not Open Yet". There is no
 * "Opening Next" any more, and never "under construction".
 */
const SEALED = MONORAIL.filter((p) => !p.open && IMAGES[p.plate]);

const SHORT = {
  arena: "The Arena",
  ops: "Ops",
  chamber: "The Chambers",
  bridge: "The Bridge",
  vault: "The Vault",
  commons: "The Commons",
};

export const SealedPlaces = () => (
  <section data-testid="sealed-places" className="border-t border-[rgba(200,160,64,0.14)] pt-14">
    <div className="flex flex-wrap items-baseline justify-between gap-4">
      <h2 className="engrave text-[clamp(1.6rem,3vw,2.5rem)] font-bold tracking-[0.06em] text-[#cfe2f0]">
        The Rest of the World
      </h2>
      <p className="mono text-[9px] uppercase tracking-[0.3em] text-[#6f8ba0]">
        {SEALED.length} Places · Not Open Yet
      </p>
    </div>
    <p className="serif mt-4 max-w-[640px] text-base leading-relaxed text-[#93aabb] md:text-lg">
      Built, lit, and standing on the map. The lamps are burning inside — the
      doors simply haven’t been opened.
    </p>

    <div className="mt-11 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
      {SEALED.map((p, i) => (
        <motion.article
          key={p.key}
          data-testid={`sealed-place-${p.key}`}
          className="overflow-hidden border border-[rgba(127,189,224,0.2)] transition-colors duration-500 hover:border-[rgba(159,216,240,0.5)]"
          style={{
            background:
              "linear-gradient(170deg, rgba(20,32,44,0.9) 0%, rgba(14,12,10,0.95) 100%)",
          }}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: (i % 3) * 0.09 }}
          whileHover={{ y: -4 }}
        >
          <div className="relative">
            <img
              src={IMAGES[p.plate]}
              alt={SHORT[p.key] || p.name}
              loading="lazy"
              decoding="async"
              className="block aspect-[3/2] w-full object-cover"
              style={{ filter: "saturate(0.42) brightness(0.52) contrast(1.05)" }}
            />
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(38% 30% at 50% 62%, rgba(255,196,96,0.3) 0%, rgba(255,180,80,0.08) 46%, rgba(10,18,26,0) 76%), linear-gradient(180deg, rgba(10,18,26,0.42) 0%, rgba(10,18,26,0.1) 40%, rgba(14,12,10,0.9) 100%)",
              }}
            />
            <p className="mono absolute left-4 top-3.5 text-[9px] uppercase tracking-[0.26em] text-[rgba(207,226,240,0.8)] drop-shadow-[0_1px_8px_rgba(0,0,0,0.9)]">
              {p.clock}
            </p>
            <p className="mono absolute right-4 top-3.5 flex items-center gap-[7px] text-[9px] uppercase tracking-[0.24em] text-[#9fd8f0] drop-shadow-[0_1px_8px_rgba(0,0,0,0.9)]">
              <motion.span
                aria-hidden
                className="h-[5px] w-[5px] rounded-full bg-[#7fbde0]"
                style={{ boxShadow: "0 0 10px 2px rgba(127,189,224,0.7)" }}
                animate={{ opacity: [0.55, 1, 0.55] }}
                transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
              />
              Not Open Yet
            </p>
          </div>
          <div className="flex flex-col gap-3 px-6 pb-6 pt-6">
            <h3 className="engrave text-xl font-semibold tracking-[0.04em] text-[#cfe2f0]">
              {SHORT[p.key] || p.name}
            </h3>
            <p className="serif text-base italic leading-snug text-[#a9c0d0]">
              {p.tagline}
            </p>
            <p className="text-sm leading-relaxed text-[#8298a8]">{p.body}</p>
            <p className="mono mt-1.5 border-t border-[rgba(127,189,224,0.14)] pt-3.5 text-[9px] uppercase tracking-[0.22em] text-[#55697a]">
              Practice · {p.practice}
            </p>
          </div>
        </motion.article>
      ))}
    </div>
  </section>
);

export default SealedPlaces;
